/**
 * Shared rules for "is this URL a safe place to send someone back to?".
 *
 * Two features ask that question — the settings back button
 * (`SettingsReturnUrlService`) and the post-login redirect
 * (`AuthReturnUrlService`) — and they must answer it identically. Keeping one
 * copy means adding a new unauthenticated route can't silently fix one caller
 * and leave the other stranding users.
 */

/**
 * Routes that must never be a return target: the unauthenticated and standalone
 * pages, which would bounce the user straight back out of the app.
 *
 * The settings section adds `/settings` to this list for its own back button —
 * it is deliberately absent here, because a deep link into settings is a
 * perfectly good place to land after logging in.
 */
export const NON_RETURNABLE_PREFIXES: readonly string[] = [
  '/login',
  '/forget-password',
  '/reset-password',
  '/maintenance',
  '/403',
];

/**
 * True when `url` is an in-app path we can safely navigate back to.
 *
 * Rejects anything that isn't a plain absolute path, which is what keeps this
 * from becoming an open redirect: `//evil.com` and `https://evil.com` are both
 * valid navigation targets to a browser, and the login page must not forward a
 * user to either just because the URL arrived in storage.
 *
 * @param extraPrefixes caller-specific exclusions, on top of the shared list.
 */
export function isReturnableUrl(
  url: string | null | undefined,
  extraPrefixes: readonly string[] = [],
): boolean {
  if (!url) {
    return false;
  }

  // Only single-slash absolute paths. `//host` is protocol-relative and leaves
  // the origin, so the second character has to be checked too.
  if (!url.startsWith('/') || url.startsWith('//')) {
    return false;
  }

  // Strip query and fragment before matching, so `/403?from=x` is caught by the
  // `/403` prefix.
  const path = url.split(/[?#]/)[0];

  // The bare root is the login page itself — returning there is a no-op loop.
  if (path === '/' || path === '') {
    return false;
  }

  return ![...NON_RETURNABLE_PREFIXES, ...extraPrefixes].some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}
